import { Dialog } from '@headlessui/react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useState, useCallback, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import TGButton from 'src/components/ui/TGButton';
import TextAreaVPO from 'src/pages/DashboardPage/vpo/ui/textArea';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { addData, updateData, deleteData } from '../../../actions/actions';
import { selectModelData } from '../../../selectors/selectors';
import { STRAPI_TOKEN } from '../../../api/config';
import StrapiFileUploader from '../FileUploader/StrapiFileUploader';
import { baseUrl } from '../../../api/strapiGop';
import { uploadFileRequest } from '../../../store/comments-files/actions';
import { selectCurrentUser } from 'src/modules/users/selector';

type ModalAddCommentProps = {
  isOpen: boolean;
  onClose: () => void;
  lastId?: number;
  mode?: 'add' | 'edit';
  editCommentDocumentId?: string;
  editCommentId?: number;
};

export default function ModalAddComment({ isOpen, onClose, lastId, mode = 'add', editCommentDocumentId, editCommentId }: ModalAddCommentProps) {
  const dispatch = useAppDispatch();
  const uploadsData = useAppSelector((state) => selectModelData(state, 'gop-uploads'));
  const currentUser: any = useAppSelector(selectCurrentUser);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [existingFiles, setExistingFiles] = useState<
    Array<{
      id: string;
      name: string;
      url: string;
      size: number;
    }>
  >([]);

  const isEditMode = mode === 'edit';
  const commentId = isEditMode ? editCommentId : lastId;
  const commentRecords = isEditMode && editCommentId ? uploadsData.filter((upload) => upload.CommentId === editCommentId) : [];
  const mainRecord = isEditMode && editCommentDocumentId ? commentRecords.find((record) => record.documentId === editCommentDocumentId) : null;

  const methods = useForm<any>({
    defaultValues: {
      comment: '',
    },
    mode: 'onChange',
  });

  const { handleSubmit, control, reset, setValue } = methods;

  useEffect(() => {
    if (isOpen && !isSubmitting && !isDeleting) {
      if (isEditMode && mainRecord) {
        setValue('comment', mainRecord.Title || '');
        if (existingFiles.length === 0) {
          const files: Array<{ id: string; name: string; url: string; size: number }> = [];

          const currentCommentRecords = uploadsData.filter((upload) => upload.CommentId === editCommentId && upload.Title === mainRecord.Title);

          currentCommentRecords.forEach((record) => {
            if (record.Docs) {
              if (Array.isArray(record.Docs)) {
                record.Docs.forEach((doc) => {
                  files.push({
                    id: doc.id,
                    name: doc.name,
                    url: doc.url,
                    size: doc.size,
                  });
                });
              } else if (record.Docs.id) {
                files.push({
                  id: record.Docs.id,
                  name: record.Docs.name,
                  url: record.Docs.url,
                  size: record.Docs.size,
                });
              }
            }
          });

          setExistingFiles(files);
        }
      } else if (!isEditMode) {
        setValue('comment', '');
        setExistingFiles([]);
      }
    }
  }, [isOpen, isEditMode, editCommentDocumentId, editCommentId, setValue, isSubmitting, isDeleting, existingFiles.length]);

  const onSubmit = useCallback(
    async (data: any) => {
      if (!commentId) {
        console.error('commentId не определен');
        return;
      }

      setIsSubmitting(true);

      try {
        const commentTitle = data.comment.trim();

        if (isEditMode && editCommentDocumentId) {
          const updateBody = {
            Title: commentTitle,
            FullName: currentUser.fullName,
            EmailUser: currentUser.email,
          };
          dispatch(updateData('gop-uploads', editCommentDocumentId, updateBody));

          const currentCommentRecords = uploadsData.filter((upload) => upload.CommentId === editCommentId && upload.Title === mainRecord?.Title);
          const recordsToUpdate = currentCommentRecords.filter((record) => record.documentId !== editCommentDocumentId);

          for (const record of recordsToUpdate) {
            if (record.documentId) {
              dispatch(updateData('gop-uploads', record.documentId, { Title: commentTitle, FullName: currentUser.fullName, EmailUser: currentUser.email }));
            }
          }

          if (selectedFiles.length > 0) {
            for (const file of selectedFiles) {
              dispatch(uploadFileRequest(file, commentId, commentTitle));
            }
          }
        } else {
          if (selectedFiles.length > 0) {
            const baseRecord = {
              Title: commentTitle,
              CommentId: commentId,
              FullName: currentUser.fullName,
              EmailUser: currentUser.email,
            };
            dispatch(addData('gop-uploads', baseRecord));
            for (const file of selectedFiles) {
              dispatch(uploadFileRequest(file, commentId, commentTitle));
            }
          } else {
            const requestBody = {
              Title: commentTitle,
              CommentId: commentId,
              FullName: currentUser.fullName,
              EmailUser: currentUser.email,
            };
            dispatch(addData('gop-uploads', requestBody));
          }
        }

        reset();
        setSelectedFiles([]);
        setExistingFiles([]);
        onClose();
      } catch (error) {
        console.error('Ошибка при отправке:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [commentId, selectedFiles, isEditMode, editCommentDocumentId, commentRecords, dispatch, reset, onClose, currentUser]
  );

  const handleFilesChange = useCallback((files: File[]) => {
    setSelectedFiles(files);
  }, []);

  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      if (!isEditMode) return;

      setIsDeleting(true);

      try {
        setExistingFiles((prev) => prev.filter((file) => file.id !== fileId));
        try {
          const response = await fetch(`${baseUrl}/${fileId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${STRAPI_TOKEN}`,
            },
            credentials: 'include'
          });

          if (!response.ok) {
            console.warn('Не удалось удалить:', response.status);
          } else {
            console.log('Файл удален');
          }
        } catch (fileError) {
          console.warn('Ошибка при удалении файла:', fileError);
        }

        const currentCommentRecords = uploadsData.filter((upload) => upload.CommentId === editCommentId && upload.Title === mainRecord?.Title);
        const recordWithFile = currentCommentRecords.find((record) => {
          if (Array.isArray(record.Docs)) {
            return record.Docs.some((doc) => doc.id === fileId);
          } else if (record.Docs?.id === fileId) {
            return true;
          }
          return false;
        });

        if (recordWithFile && recordWithFile.documentId) {
          let remainingFiles = [];

          if (Array.isArray(recordWithFile.Docs)) {
            remainingFiles = recordWithFile.Docs.filter((doc) => doc.id !== fileId);
          } else if (recordWithFile.Docs?.id !== fileId) {
            remainingFiles = [recordWithFile.Docs];
          }

          if (remainingFiles.length === 0) {
            dispatch(deleteData('gop-uploads', recordWithFile.documentId));
          } else {
            dispatch(
              updateData('gop-uploads', recordWithFile.documentId, {
                Docs: remainingFiles.length === 1 ? remainingFiles[0] : remainingFiles,
              })
            );
          }
        }
      } catch (error) {
        console.error('Ошибка при удалении файла:', error);
      } finally {
        setTimeout(() => {
          setIsDeleting(false);
        }, 500);
      }
    },
    [isEditMode, editCommentId, mainRecord?.Title, uploadsData, dispatch, STRAPI_TOKEN]
  );

  const handleClose = useCallback(() => {
    reset({ comment: '' });
    setSelectedFiles([]);
    setExistingFiles([]);
    setIsDeleting(false);
    onClose();
  }, [reset, onClose]);

  const formatFileSize = (sizeInKB: number) => {
    if (!sizeInKB || sizeInKB === 0) return '0 MB';
    const sizeInMB = sizeInKB / 1024;
    return `${sizeInMB.toFixed(2)} MB`;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={handleClose} className='fixed inset-0 z-50 overflow-y-auto' static>
      <div className='fixed inset-0 bg-black bg-opacity-50' aria-hidden='true' />
      <div className='fixed inset-0 flex items-center justify-center p-4'>
        <Dialog.Panel className='w-full max-w-[70%] transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
          <div className='flex justify-between items-center mb-4'>
            <Dialog.Title as='h3' className='text-lg font-medium leading-6 text-gray-900'>
              {isEditMode ? 'Редактирование комментария' : 'Добавление комментария'}
            </Dialog.Title>
            <button type='button' className='rounded-md text-gray-400 hover:text-gray-500 focus:outline-none' onClick={handleClose} disabled={isSubmitting}>
              <span className='sr-only'>Закрыть</span>
              <XMarkIcon className='h-6 w-6' aria-hidden='true' />
            </button>
          </div>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='mt-2'>
                <div className='space-y-4'>
                  <div>
                    <Controller
                      control={control}
                      name='comment'
                      rules={{
                        required: 'Обязательное поле',
                        minLength: {
                          value: 1,
                          message: 'Комментарий не может быть пустым',
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextAreaVPO
                          dataField={{
                            type: 'textArea',
                            require: true,
                            width: 'w-full',
                            title: 'Комментарий',
                            key: 'comment',
                            placeholder: 'Введите ваш комментарий',
                          }}
                          value={field.value || ''}
                          onChange={field.onChange}
                          errors={{ comment: error }}
                          minHeight={70}
                        />
                      )}
                    />
                  </div>
                  {isEditMode && existingFiles.length > 0 && (
                    <div>
                      <h4 className='text-sm font-medium text-gray-900 mb-2'>Прикрепленные файлы:</h4>
                      <div className='space-y-2'>
                        {existingFiles.map((file, index) => (
                          <div key={index} className='flex items-center justify-between bg-gray-50 rounded-lg p-3'>
                            <div className='flex items-center space-x-3'>
                              <div>
                                <span className='text-sm font-medium text-gray-900'>{file.name.length > 30 ? `${file.name.substring(0, 30)}...${file.name.split('.').pop()}` : file.name}</span>
                                <span className='text-sm text-gray-500 ml-2'>({formatFileSize(file.size)})</span>
                              </div>
                            </div>
                            <button
                              type='button'
                              onClick={() => handleDeleteFile(file.id)}
                              className='p-2 text-gray-400 hover:text-red-600 hover:bg-gray-200 rounded'
                              title='Удалить файл'
                              disabled={isSubmitting}
                            >
                              <TrashIcon className='w-4 h-4' />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    {isEditMode && <h4 className='text-sm font-medium text-gray-900 mb-2'>Добавить новые файлы:</h4>}
                    <StrapiFileUploader onFilesChange={handleFilesChange} commentId={commentId} disabled={isSubmitting} isEditMode={isEditMode} />
                  </div>
                </div>
              </div>
              <div className='mt-5 flex justify-between'>
                <TGButton type='button' onClick={handleClose} disabled={isSubmitting}>
                  Отмена
                </TGButton>
                <TGButton type='submit' color='bluedark' disabled={isSubmitting}>
                  {isSubmitting ? 'Сохранение...' : isEditMode ? 'Сохранить изменения' : 'Сохранить'}
                </TGButton>
              </div>
            </form>
          </FormProvider>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
